import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
} from 'react'
import { IPerioChart } from '@dataTypes/chartingFeature.types'
import { DataGridComponent } from '@components/common'
import { EmptyState } from '@components/ui'
import ChartingDeleteItemModal from '@components/pages/Charting/Modals/ChartingDeleteItemModal.component'
import ChartingDiscardInProgressModal from '@components/pages/Charting/Modals/ChartingDiscardInProgressModal.component'
import ChartingEditChartModal from '@components/pages/Charting/Modals/ChartingEditChartModal.component'
import {
  ActionEventArgs,
  ColumnModel,
  GridComponent,
  RowSelectEventArgs,
} from '@syncfusion/ej2-react-grids'
import { formatProviderDisplayName } from '@utils/provider-display.utils'
import { useStore } from 'react-redux'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import type { RootState } from '@store/index'
import { useChartHistoryGridSelection } from '@hooks/useChartHistoryGridSelection'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import { deletePerioChart } from '@store/actions'
import {
  setCreatePerioChart,
  setPerioUpdated,
  setPreferCreatePerioChart,
  setSelectedPerioChart,
} from '@store/reducers/charting.reducer'
import { syncChartHistoryEditButtons } from '@utils/chart-history-selection.utils'
import { getAlignedPerioEditChartId } from '@utils/active-perio-chart.utils'

interface PerioHistoryProps {
  onViewNote: (id: string, openFirstNote?: boolean) => void
  onSelectHistory: (chart: RowSelectEventArgs) => void
  gridRef: React.RefObject<GridComponent>
}

const PerioHistory: React.FC<PerioHistoryProps> = ({
  onViewNote,
  onSelectHistory,
  gridRef,
}) => {
  const isMountedRef = useRef<boolean>(true)
  const store = useStore<RootState>()
  const dispatch = useAppDispatch()
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const perioCharts = useAppSelector((state) => state.charting.perioCharts)
  const chartingLoading = useAppSelector((state) => state.charting.loading)
  const isPerioUpdated = useAppSelector(
    (state) => state.charting.isPerioUpdated,
  )

  const createPerioChart = useAppSelector(
    (state) => state.charting.createPerioChart,
  )
  const preferCreatePerioChart = useAppSelector(
    (state) => state.charting.preferCreatePerioChart,
  )
  const selectedPerioChart = useAppSelector(
    (state) => state.charting.selectedPerioChart,
  )
  const onViewNoteRef = useRef(onViewNote)
  onViewNoteRef.current = onViewNote
  const editingChartId = useMemo(
    (): string =>
      getAlignedPerioEditChartId(
        selectedPerioChart,
        createPerioChart,
        preferCreatePerioChart,
      ),
    [createPerioChart, preferCreatePerioChart, selectedPerioChart],
  )
  const editingChartIdRef = useRef(editingChartId)
  editingChartIdRef.current = editingChartId

  const [showEditConfirm, setShowEditConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [chartToEdit, setChartToEdit] = useState<IPerioChart | null>(null)
  const [chartToDelete, setChartToDelete] = useState<IPerioChart | null>(null)

  const historyData = useMemo(
    () =>
      selectedPatient && !chartingLoading
        ? perioCharts
            .filter((p) => p.patient_id === selectedPatient.patient_id)
            .map((p) => ({
              ...p,
              date: p.date_time_created_at,
              provider_name: formatProviderDisplayName(p.provider),
            }))
        : [],
    [selectedPatient, chartingLoading, perioCharts],
  )

  useEffect(() => {
    isMountedRef.current = true
    return (): void => {
      isMountedRef.current = false
    }
  }, [])

  const {
    selectChartRowIfAvailable,
    handleRowSelected,
    handleRowDeselected,
    handleRecordClick,
    handleRowDataBound,
    handleDataBound,
    handleGridActionComplete,
  } = useChartHistoryGridSelection({
    gridRef,
    historyData,
    selectedChartId: selectedPerioChart?.chart_id?.trim() ?? '',
    onSelectHistory,
    isMountedRef,
    canSyncSelection: Boolean(selectedPatient),
    getSelectedChartId: (): string =>
      store.getState().charting.selectedPerioChart?.chart_id?.trim() ?? '',
  })

  const handleHistoryDataBound = useCallback((): void => {
    handleDataBound()
    syncChartHistoryEditButtons(gridRef.current, editingChartId)
  }, [editingChartId, gridRef, handleDataBound])

  const handleHistoryActionComplete = useCallback(
    (args: ActionEventArgs): void => {
      handleGridActionComplete(args)
      syncChartHistoryEditButtons(gridRef.current, editingChartId)
    },
    [editingChartId, gridRef, handleGridActionComplete],
  )

  useLayoutEffect(() => {
    syncChartHistoryEditButtons(gridRef.current, editingChartId)
  }, [editingChartId, gridRef, historyData])

  useEffect(() => {
    if (!preferCreatePerioChart) {
      return
    }
    setShowEditConfirm(false)
    setChartToEdit(null)
  }, [preferCreatePerioChart])

  /** Keep history selection aligned with create chart so the side panel exits view-only mode. */
  const confirmEditChart = useCallback(
    (chart: IPerioChart): void => {
      dispatch(setCreatePerioChart({ ...chart }))
      dispatch(setSelectedPerioChart({ ...chart }))
      dispatch(setPreferCreatePerioChart(false))
      dispatch(setPerioUpdated(false))
      setShowEditConfirm(false)

      const chartId = chart.chart_id
      if (!chartId) {
        return
      }
      selectChartRowIfAvailable(chartId)
    },
    [dispatch, selectChartRowIfAvailable],
  )

  const handleDeleteChart = useCallback((chart: IPerioChart): void => {
    setShowDeleteConfirm(true)
    setChartToDelete(chart)
  }, [])

  const extractPerioChartProperties = useCallback(
    (chart: IPerioChart & Record<string, unknown>): IPerioChart => {
      return {
        chart_id: chart.chart_id,
        patient_id: chart.patient_id,
        provider_id: chart.provider_id,
        clinic_id: chart.clinic_id,
        title: chart.title,
        description: chart.description,
        perio_data: chart.perio_data,
        patient: chart.patient,
        provider: chart.provider,
        notes: chart.notes,
        date_time_created_at: chart.date_time_created_at,
        date_time_updated_at: chart.date_time_updated_at,
      }
    },
    [],
  )

  const handleEditChart = useCallback((chart: IPerioChart): void => {
    setShowEditConfirm(true)
    setChartToEdit({ ...chart })
  }, [])

  const columns: ColumnModel[] = useMemo(
    (): ColumnModel[] => [
      {
        field: 'date',
        headerText: 'Date',
        template: (row: IPerioChart): JSX.Element => (
          <span>
            {new Date(row.date_time_created_at ?? '').toLocaleDateString()}
          </span>
        ),
      },
      {
        field: 'title',
        headerText: 'Chart name',
      },
      {
        field: 'provider_name',
        headerText: 'Provider',
      },
      {
        field: '',
        headerText: 'Notes',
        headerTextAlign: 'Center',
        textAlign: 'Center',
        template: (row: IPerioChart): JSX.Element =>
          !!row.notes?.length ? (
            <button
              className="action-button action-button-view"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onViewNoteRef.current(row.chart_id, true)
              }}
              type="button"
            >
              <i className="mgc_document_fill" />
            </button>
          ) : (
            <span>-</span>
          ),
        width: '50px',
      },
      {
        field: '',
        headerText: 'Actions',
        headerTextAlign: 'Center',
        textAlign: 'Center',
        template: (row: IPerioChart): JSX.Element => (
          <div
            onClick={(e) => e.stopPropagation()}
            data-chart-history-id={row.chart_id ?? ''}
          >
            <ActionButtons
              onEdit={() => {
                handleEditChart(
                  extractPerioChartProperties(
                    row as IPerioChart & Record<string, unknown>,
                  ),
                )
              }}
              onDelete={() => {
                handleDeleteChart(row)
              }}
              disableEdit={row.chart_id === editingChartIdRef.current}
            />
          </div>
        ),
        width: '50px',
      },
    ],
    [extractPerioChartProperties, handleDeleteChart, handleEditChart],
  )

  const showEmptyState = !chartingLoading && historyData.length === 0

  return (
    <>
      <div className="perio-history">
        {!showEmptyState && (
          <div className="perio-history-title mb-1">Perio history</div>
        )}
        <div className="history-grid-container">
          {showEmptyState ? (
            <EmptyState
              className="charting-history-empty"
              iconClass="mgc mgc_document_fill"
              title="No perio charts found"
              description={
                selectedPatient
                  ? "You don't have any perio charts on record yet."
                  : 'Select a patient to view their charts.'
              }
            />
          ) : (
            <DataGridComponent
              key={selectedPatient?.patient_id || 'no-patient'}
              ref={gridRef}
              allowPaging={true}
              data={historyData}
              columns={columns}
              pageSize={10}
              selectionSettings={{
                type: 'Single',
                mode: 'Row',
                enableToggle: true,
              }}
              rowSelected={handleRowSelected}
              rowDeselected={handleRowDeselected}
              recordClick={handleRecordClick}
              customCSS="datatable-table perio-history-table"
              dataBound={handleHistoryDataBound}
              rowDataBound={handleRowDataBound}
              onActionComplete={handleHistoryActionComplete}
              isLoading={false}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          )}
        </div>
      </div>

      <ChartingDeleteItemModal
        show={showDeleteConfirm}
        itemName={chartToDelete?.title ?? ''}
        onCancel={() => setShowDeleteConfirm(false)}
        onDelete={() => {
          dispatch(deletePerioChart(chartToDelete?.chart_id ?? ''))
          setShowDeleteConfirm(false)
        }}
      />
      {isPerioUpdated && chartToEdit ? (
        <ChartingDiscardInProgressModal
          show={showEditConfirm}
          chartName={chartToEdit.title ?? ''}
          variant="perio"
          onKeepEditing={() => setShowEditConfirm(false)}
          onDiscard={() => {
            if (!chartToEdit) return
            confirmEditChart(chartToEdit)
          }}
        />
      ) : (
        <ChartingEditChartModal
          show={showEditConfirm}
          chartName={chartToEdit?.title ?? ''}
          actionVerb="edit"
          onCancel={() => setShowEditConfirm(false)}
          onContinue={() => {
            if (!chartToEdit) return
            confirmEditChart(chartToEdit)
          }}
        />
      )}
    </>
  )
}

export default PerioHistory
